import { useCallback, useEffect, useMemo, useState } from 'react';
import { recordBuildAnswer } from '../services/api';

/**
 * Walks a build script: which question is on screen, what has been answered,
 * and what the trail has recorded.
 *
 * <h3>It records, it does not predict</h3>
 *
 * Only answered steps become nodes. Platform steps are appended by whoever
 * performs them, once their call has returned — this hook never writes one,
 * because it does not know whether the work happened.
 *
 * <h3>Resuming</h3>
 *
 * A `seed` of `{answers, at}` reopens a build that was saved half-finished. The
 * trail is rebuilt from the answers rather than restored from the draft, so
 * what the person sees above the question is the script's own words for what
 * they said, not a stale copy of them.
 *
 * It refuses to resume at or past the commit. A draft only exists while nothing
 * has been created; seeding past it would put somebody in front of a question
 * about an agent this session knows nothing about — including the one that
 * registers it, which would register a second.
 *
 * @param {Array}  script steps from data/buildScripts.js
 * @param {Object} opts   `{seed: {answers, at}}` to resume a saved build
 */
export function useBuildFlow(script = [], { seed = null, resumeFrom = null, agentId = null } = {}) {
  /* How a saved build becomes a starting point. Pure, so it can be applied at
     mount or when the person accepts the nudge that offers it. */
  const resolve = useCallback((from) => {
    const at = script.findIndex((st) => st.key === from?.at);
    const commitAt0 = script.findIndex((st) => st.commit);
    if (at <= 0) return { i: 0, answers: {}, log: [] };
    if (commitAt0 >= 0 && at > commitAt0) return { i: 0, answers: {}, log: [] };
    const answered = script.slice(0, at);
    return {
      i: at,
      answers: { ...(from.answers || {}) },
      /* Built from the script, exactly as `answer` builds one, so a reopened
         trail and a walked one cannot read differently. */
      log: answered.map((st, n) => {
        const v = from.answers?.[st.key];
        const node = typeof st.node === 'function' ? st.node(v) : st.node;
        return { id: `${st.key}-${n}`, stepKey: st.key, stage: st.stage,
          icon: st.icon, ...node };
      }),
    };
  }, [script]);

  /**
   * Re-entering on an agent that already exists.
   *
   * Distinct from `seed`, and deliberately so. A draft resumes from answers
   * somebody gave and must never land past the commit — one that did would
   * re-register the agent. This resumes from what the PLATFORM says is true,
   * is allowed past the commit precisely because the agent is already there,
   * and arrives with its trail built from those reads rather than from node()
   * applied to answers nobody gave.
   */
  const reentry = useMemo(() => {
    const at = script.findIndex((st) => st.key === resumeFrom?.at);
    if (at <= 0) return null;
    return { i: at, answers: {}, log: resumeFrom.log || [] };
    // eslint-disable-next-line
  }, [script, resumeFrom]);

  const opening = reentry || resolve(seed);

  const [answers, setAnswers] = useState(opening.answers);
  const [log, setLog] = useState(opening.log);
  const [i, setI] = useState(opening.i);

  /**
   * A seed that arrives after mount — the person accepted the nudge offering a
   * saved build.
   *
   * Only while the sequence is untouched. Landing a draft on a walk somebody is
   * part-way through would move the question out from under them, and the
   * answers on screen are theirs, not the draft's.
   */
  const untouched = i === 0 && log.length === 0;
  useEffect(() => {
    if (!seed || !untouched) return;
    const next = resolve(seed);
    if (next.i === 0) return;
    setAnswers(next.answers);
    setLog(next.log);
    setI(next.i);
  }, [seed, untouched, resolve]);

  /**
   * And the same for a re-entry, which ALWAYS arrives after mount.
   *
   * This hook is called unconditionally by the page, so it is mounted long
   * before anybody clicks to pick a build back up. Reading `resumeFrom` only
   * in the useState initialisers meant it was read once, before it could
   * possibly exist — the spine opened on question one with an empty trail and
   * offered to bind a framework to an agent registered days earlier.
   *
   * The draft seed had this bug and this fix; the second path did not inherit
   * it because the fix lived in an effect written for the first.
   */
  useEffect(() => {
    if (!reentry || !untouched) return;
    setAnswers(reentry.answers);
    setLog(reentry.log);
    setI(reentry.i);
  }, [reentry, untouched]);

  const asks = useMemo(() => script.filter((s) => s.ask), [script]);
  const step = script[i] || null;
  const complete = i >= script.length;

  /* The ask is the step's public face, so it carries the step's own markers:
     whether answering it commits anything, and which journey stage the agent
     is at while it is on screen. A caller that had to reach back into the
     script for those is a caller that can silently drop them. */
  /* Where the point of no return is, and whether it has been passed. Derived
     from the script's own marker so moving the commit moves the guard with it.
     A script with no commit step never freezes anything. */
  const commitAt = useMemo(() => script.findIndex((s) => s.commit), [script]);
  const committed = commitAt >= 0 && i > commitAt;

  const ask = step?.ask
    ? {
      ...step.ask,
      key: step.key,
      commit: step.commit === true,
      stage: step.stage,
      /* The icon travels with the step. The trail used to look it up by the
         answer's key against the Register step list, which only works while
         the two spell every step the same way — and they do not. */
      icon: step.icon,
      number: asks.findIndex((s) => s.key === step.key) + 1,
      total: asks.length,
    }
    : null;

  const answer = useCallback((value) => {
    const s = script[i];
    if (!s) return;
    const node = typeof s.node === 'function' ? s.node(value) : s.node;
    /* Keep it with the agent, not only in this tab.
     *
     * The log below is browser state: leave and come back through "Continue the build" and the
     * trail is rebuilt from what the PLATFORM can confirm, which is far less than was asked.
     * Measured on one agent: fourteen answers came back as eight nodes, and Register — where it
     * was named, pointed at a framework, wired up and handed its keys — came back as one line.
     * For a platform whose product is provenance, what somebody was asked and what they answered
     * belongs to the agent.
     *
     * The NODE travels, never `value`. A step's value is whatever that step collected; the
     * credentials step's is key labels today only because CredentialsAsk maps them that way, and
     * that is one edit from writing minted keys into a readable table from the step whose own
     * question reads "Save its keys. They are shown once."
     *
     * Nothing is kept before the agent exists — those steps are answered pre-commit and the
     * draft already holds them — and a record that will not write must not take the walk down
     * with it: the answer stands either way. */
    if (agentId) {
      const ordinal = asks.findIndex((a2) => a2.key === s.key) + 1;
      Promise.resolve()
        .then(() => recordBuildAnswer(agentId, {
          stepKey: s.key,
          stage: s.stage || null,
          ordinal: ordinal > 0 ? ordinal : null,
          question: s.ask?.question || null,
          label: node?.title || null,
          detail: node?.text || null,
        }))
        .catch(() => { /* the walk is not held up by its own record */ });
    }
    setAnswers((prev) => ({ ...prev, [s.key]: value }));
    /* `stepKey` is what lets the trail offer to change this step later. It is
       stamped from the script rather than inferred from the node's wording,
       which would break the moment a node's title is edited. */
    /* The node carries the stage it belongs to. The trail groups by it, so a
       node without one would fall out of the journey it is part of — which is
       how Identity and Intent came to look like they were never visited: their
       nodes were in the list, just not under a heading that said so. */
    setLog((prev) => [...prev,
      { id: `${s.key}-${prev.length}`, stepKey: s.key, stage: s.stage,
        icon: s.icon, ...node }]);
    setI((n) => n + 1);
  }, [script, i, agentId, asks]);

  /**
   * Go back to an answered step, before anything has been committed.
   *
   * Refused outright for any step at or before the commit once the commit has
   * happened — see the guard below for why.
   *
   * The nodes below it record answers that have not been given again, so the
   * trail is cut back to this step — a record must not show things that are no
   * longer true. The answers themselves are kept, so the reopened question
   * shows what was chosen rather than an empty field.
   */
  const reopen = useCallback((key) => {
    const at = script.findIndex((s) => s.key === key);
    if (at < 0 || at >= i) return;
    /* Nothing that produced the agent may be reopened once it exists.
       Answering the commit step a second time fires the onboard a second time —
       a second agent row, a second registration and a second set of minted
       keys, with the first orphaned and nobody told. The console draws the same
       line: its Back button is disabled past the mint because the credentials
       are already in the database. The rule lives here rather than in the trail
       because the hook owns the sequence; a view that merely stops drawing the
       link leaves the door open for the next caller. */
    if (committed && at <= commitAt) return;
    setI(at);
    setLog((prev) => {
      const cut = prev.findIndex((e) => e.stepKey === key);
      return cut < 0 ? prev : prev.slice(0, cut);
    });
  }, [script, i, committed, commitAt]);

  /**
   * Whether a step can still be reopened — the same rule `reopen` enforces,
   * asked rather than re-derived. A view that kept its own copy of where the
   * commit is would offer a link the hook then declines.
   */
  const canReopen = useCallback((key) => {
    const at = script.findIndex((s) => s.key === key);
    if (at < 0 || at >= i) return false;
    return !(committed && at <= commitAt);
  }, [script, i, committed, commitAt]);

  /** Append a step the platform performed, once its call has returned. */
  const record = useCallback((node) => {
    setLog((prev) => [...prev, { id: `did-${prev.length}`, ...node }]);
  }, []);

  return { ask, answers, log, complete, committed, canReopen, answer, record, reopen };
}

export default useBuildFlow;
